/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, FC, useRef, useEffect } from "react";
import "../css/MonthYearPicker.css";
import dropdownIcon from "../../assets/images/month-year-picker-dropdown.svg";
import { CALENDAR_RESERVATION_TYPE } from "utils/constants";

interface EventsMonthYearPickerProps {
  selectedDate: Date;
  setSelectedDate: (date: string) => void;
  setCurrentDate: boolean;
  nextAvailableDate?: Date;
  selectedTab?: string;
}

const months: string[] = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const years: number[] = Array.from({ length: 100 }, (_, i) => 2000 + i);

const EventsMonthYearPicker: FC<EventsMonthYearPickerProps> = ({
  selectedDate,
  setSelectedDate,
  setCurrentDate,
  nextAvailableDate,
  selectedTab,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(
    selectedDate.getMonth(),
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    selectedDate.getFullYear(),
  );
  const pickerRef = useRef<HTMLDivElement>(null);

  // --- Current month + next 12 months ---
  const now = new Date();
  const allowedMonths: { month: number; year: number }[] = [];

  for (let i = 0; i <= 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    allowedMonths.push({
      month: date.getMonth(),
      year: date.getFullYear(),
    });
  }

  const allowedYears = Array.from(new Set(allowedMonths.map((m) => m.year)));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isFirstMount = useRef(true);

  useEffect(() => {
    // Only reset to today when this picker is newly activated (i.e. the user
    // switched to a monthly tab). Skip on the initial mount so back-navigation
    // restores the previously selected date instead of jumping to today.
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    const now = new Date();
    setSelectedMonth(now.getMonth());
    setSelectedYear(now.getFullYear());
    setSelectedDate(now.toISOString());
  }, [setCurrentDate]);

  // Sync internal state whenever the parent changes selectedDate externally
  // (e.g. auto-jump to next available month).
  useEffect(() => {
    setSelectedMonth(selectedDate.getMonth());
    setSelectedYear(selectedDate.getFullYear());
  }, [selectedDate]);

  const togglePicker = (): void => setOpen(!open);

  const handleMonthSelect = (index: number): void => {
    setSelectedMonth(index);

    const now = new Date();
    let newDate: Date;

    // If user selects current month in current year, keep today's date
    if (selectedYear === now.getFullYear() && index === now.getMonth()) {
      newDate = now;
    } else {
      // For future months, use 1st of that month
      newDate = new Date(selectedYear, index, 1);
    }

    setSelectedDate(newDate.toISOString());
    setOpen(false);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const year = parseInt(e.target.value, 10);

    // Find allowed months for that year
    const allowedForYear = allowedMonths.filter((m) => m.year === year);
    const isCurrentMonthAllowed = allowedForYear.some(
      (m) => m.month === selectedMonth,
    );

    let newSelectedMonth = selectedMonth;

    // If current selectedMonth is not allowed in new year, switch to first allowed
    if (!isCurrentMonthAllowed && allowedForYear.length > 0) {
      newSelectedMonth = allowedForYear[0].month;
      setSelectedMonth(newSelectedMonth);
    }

    setSelectedYear(year);

    const now = new Date();
    let newDate: Date;

    if (year === now.getFullYear() && newSelectedMonth === now.getMonth()) {
      newDate = now;
    } else {
      newDate = new Date(year, newSelectedMonth, 1);
    }

    setSelectedDate(newDate.toISOString());
  };

  return (
    <div className="month-year-picker-container" ref={pickerRef}>
      <div className="month-year-picker-button" onClick={togglePicker}>
        <div>
          {selectedTab === CALENDAR_RESERVATION_TYPE.RETREAT
            ? selectedYear
            : months[selectedMonth]}
        </div>
        <img src={dropdownIcon} alt="dropdown-logo" />
      </div>

      {open && (
        <div className="month-year-picker-dropdown">
          <span>Select Year & Month</span>
          <select
            className="year-selector"
            value={selectedYear}
            onChange={handleYearChange}
          >
            {years.map((year: number) => (
              <option
                key={year}
                value={year}
                disabled={!allowedYears.includes(year)}
              >
                {year}
              </option>
            ))}
          </select>

          <div className="month-grid">
            {months.map((month: string, index: number) => {
              const isAllowed = allowedMonths.some(
                (m) => m.month === index && m.year === selectedYear,
              );
              return (
                <div
                  key={month}
                  className={`month-cell ${
                    index === selectedMonth ? "selected" : ""
                  } ${!isAllowed ? "disabled" : ""}`}
                  onClick={() => isAllowed && handleMonthSelect(index)}
                  style={
                    !isAllowed ? { pointerEvents: "none", opacity: 0.5 } : {}
                  }
                >
                  {month}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsMonthYearPicker;
